import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import {
  installSoftware,
  uninstallSoftware,
  setExpertSkill,
  setTranslatorLanguage,
  setDatabaseTopic,
  setVirtualCrewRoles,
  setVirtualGunnerRoles,
} from "../../../../store/actions/shipBuilderActions";
import {
  getSoftwareCategories,
  getSoftwareByCategory,
  validateSoftwareSelection,
  calculateSoftwareCost,
} from "../../../../utils/calculations/softwareCalculations";
import { SoftwareSelection } from "../../../../types/software";
import { formatCurrency } from "../../../../utils/formatting";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";

export const BreakawaySoftwareSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const software: SoftwareSelection = useAppSelector(
    (state) => state.shipBuilder.software,
  );
  const computer = useAppSelector((state) => state.shipBuilder.computer);
  const shipTechLevel = useAppSelector((state) => state.shipBuilder.techLevel);
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  const handleInstallSoftware = (packageId: string, level: number) => {
    dispatch(installSoftware({ packageId, level }));
  };

  const handleUninstallSoftware = (packageId: string, level: number, instanceId?: string) => {
    dispatch(uninstallSoftware({ packageId, level, instanceId }));
  };

  const handleSetExpertSkill = (instanceId: string, skill: string) => {
    dispatch(setExpertSkill({ instanceId, skill }));
  };

  const handleSetTranslatorLanguage = (instanceId: string, language: string) => {
    dispatch(setTranslatorLanguage({ instanceId, language }));
  };

  const handleSetDatabaseTopic = (instanceId: string, topic: string) => {
    dispatch(setDatabaseTopic({ instanceId, topic }));
  };

  const handleSetVirtualCrewRoles = (instanceId: string, roles: string[]) => {
    dispatch(setVirtualCrewRoles({ instanceId, roles }));
  };

  const handleSetVirtualGunnerRoles = (instanceId: string, roles: string[]) => {
    dispatch(setVirtualGunnerRoles({ instanceId, roles }));
  };

  const isIncludedSoftware = (packageId: string): boolean => {
    // These software packages come included with every ship
    const includedPackages = ["manoeuvre", "intellect", "library"];
    return includedPackages.includes(packageId);
  };

  const categories = ["All", ...getSoftwareCategories()];
  const filteredPackages =
    selectedCategory === "All"
      ? getSoftwareByCategory("") // This will return all packages
      : getSoftwareByCategory(selectedCategory);

  // Sort packages with included software first, then alphabetically
  const sortedPackages = [...filteredPackages].sort((a, b) => {
    const aIsIncluded = isIncludedSoftware(a.id);
    const bIsIncluded = isIncludedSoftware(b.id);
    
    // If one is included and the other isn't, included comes first
    if (aIsIncluded && !bIsIncluded) return -1;
    if (!aIsIncluded && bIsIncluded) return 1;
    
    // If both are included or both are not included, sort alphabetically
    return a.name.localeCompare(b.name);
  });

  const validation = validateSoftwareSelection(
    software || { installed: [], running: [] },
    computer,
    shipTechLevel,
    tlOverrideActive
  );

  const isSoftwareInstalled = (packageId: string, level: number): boolean => {
    return (software?.installed || []).some(
      (s) => s.packageId === packageId && s.level === level && s.installed,
    );
  };

  const getInstalledInstances = (packageId: string, level: number) => {
    return (software?.installed || []).filter(
      (s) => s.packageId === packageId && s.level === level && s.installed,
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Breakaway Software Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Configure software packages with multiple instances and skill specialties
        </p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
          Breakaway Software Rules
        </h3>
        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <li>• Each software package can have multiple instances for different configurations</li>
          <li>• Expert software can have different skills per instance</li>
          <li>• Translator software can handle different languages per instance</li>
          <li>• Database software can cover different topics per instance</li>
        </ul>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === category
                ? "bg-blue-600 text-white"
                : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedPackages.map((pkg) => {
          const availableLevels = pkg.maxLevel !== undefined 
            ? Array.from({ length: pkg.maxLevel + 1 }, (_, i) => i)
            : [0];

          return (
            <div key={pkg.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                {pkg.name}
                {isIncludedSoftware(pkg.id) && (
                  <span className="ml-2 px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded">
                    Included
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <div>Category: {pkg.category}</div>
                <div>TL: {pkg.tl}</div>
                {pkg.maxLevel !== undefined && (
                  <div>Levels: 0-{pkg.maxLevel}</div>
                )}
              </div>
              
              {/* Level Selection */}
              <div className="mb-3">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      const level = parseInt(e.target.value);
                      handleInstallSoftware(pkg.id, level);
                      e.target.value = "";
                    }
                  }}
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs dark:bg-gray-600 dark:text-gray-100"
                  defaultValue=""
                >
                  <option value="">Select level...</option>
                  {availableLevels.map((level) => {
                    const levelInfo = pkg.levels?.find(l => l.level === level) || pkg;
                    const isInstalled = isSoftwareInstalled(pkg.id, level);
                    
                    if (isInstalled) return null;
                    
                    return (
                      <option key={level} value={level}>
                        Level {level} • Cost: {formatCurrency(levelInfo.cost * 1000000)} Cr
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Installed Instances */}
              {availableLevels.some(level => isSoftwareInstalled(pkg.id, level)) && (
                <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Installed Instances:</div>
                  {availableLevels.map((level) => {
                    const instances = getInstalledInstances(pkg.id, level);
                    return instances.map((instance, index) => (
                      <div key={instance.instanceId} className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-700">
                        <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">
                          Instance {index + 1} (Level {level})
                        </div>
                        
                        {/* Expert Software Configuration */}
                        {pkg.id === "expert" && (
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="Enter skill (e.g., Pilot, Engineer, Gunner)"
                              value={software?.expertSkills?.[instance.instanceId || ''] || ""}
                              onChange={(e) => handleSetExpertSkill(instance.instanceId || '', e.target.value)}
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100"
                            />
                          </div>
                        )}

                        {/* Translator Software Configuration */}
                        {pkg.id === "translator" && (
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="Enter language (e.g., Vilani, Aslan, Vargr)"
                              value={software?.translatorLanguages?.[instance.instanceId || ''] || ""}
                              onChange={(e) => handleSetTranslatorLanguage(instance.instanceId || '', e.target.value)}
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100"
                            />
                          </div>
                        )}

                                                 {/* Database Software Configuration */}
                         {pkg.id === "database" && (
                           <div className="space-y-2">
                             <input
                               type="text"
                               placeholder="Enter database topic (e.g., Starship Engineering, Alien Cultures)"
                               value={software?.databaseTopics?.[instance.instanceId || ''] || ""}
                               onChange={(e) => handleSetDatabaseTopic(instance.instanceId || '', e.target.value)}
                               className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100"
                             />
                           </div>
                         )}

                         {/* Virtual Crew Configuration */}
                         {pkg.id === "virtualCrew" && (
                           <div className="space-y-2">
                             <label className="text-xs font-medium text-blue-800 dark:text-blue-200">
                               Crew Roles to Replace:
                             </label>
                             <div className="space-y-1">
                               {["pilot", "sensorOperator"].map((role) => (
                                 <label key={role} className="flex items-center">
                                   <input
                                     type="checkbox"
                                     checked={software?.virtualCrewRoles?.[instance.instanceId || '']?.includes(role) || false}
                                     onChange={(e) => {
                                       const currentRoles = software?.virtualCrewRoles?.[instance.instanceId || ''] || [];
                                       const newRoles = e.target.checked
                                         ? [...currentRoles, role]
                                         : currentRoles.filter(r => r !== role);
                                       handleSetVirtualCrewRoles(instance.instanceId || '', newRoles);
                                     }}
                                     className="mr-2"
                                   />
                                   <span className="text-xs text-blue-700 dark:text-blue-300 capitalize">
                                     {role.replace(/([A-Z])/g, ' $1').trim()}
                                   </span>
                                 </label>
                               ))}
                             </div>
                           </div>
                         )}

                         {/* Virtual Gunner Configuration */}
                         {pkg.id === "virtualGunner" && (
                           <div className="space-y-2">
                             <label className="text-xs font-medium text-blue-800 dark:text-blue-200">
                               Gunner Roles to Replace:
                             </label>
                             <div className="space-y-1">
                               {["turret", "barbette", "bay", "screen"].map((role) => (
                                 <label key={role} className="flex items-center">
                                   <input
                                     type="checkbox"
                                     checked={software?.virtualGunnerRoles?.[instance.instanceId || '']?.includes(role) || false}
                                     onChange={(e) => {
                                       const currentRoles = software?.virtualGunnerRoles?.[instance.instanceId || ''] || [];
                                       const newRoles = e.target.checked
                                         ? [...currentRoles, role]
                                         : currentRoles.filter(r => r !== role);
                                       handleSetVirtualGunnerRoles(instance.instanceId || '', newRoles);
                                     }}
                                     className="mr-2"
                                   />
                                   <span className="text-xs text-blue-700 dark:text-blue-300 capitalize">
                                     {role}
                                   </span>
                                 </label>
                               ))}
                             </div>
                           </div>
                         )}

                         <button
                           onClick={() => handleUninstallSoftware(pkg.id, level, instance.instanceId)}
                           className="mt-2 px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                         >
                           Remove Instance
                         </button>
                       </div>
                     ));
                   })}
                 </div>
               )}
             </div>
           );
         })}
       </div>

       {/* Software Summary */}
       <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-4 rounded-lg">
         <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Software Summary</h4>
         <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
           <div>
             <span className="font-medium text-gray-600 dark:text-gray-300">Installed:</span>
             <div className="text-gray-900 dark:text-gray-100">
               {(software?.installed || []).filter(s => s.installed).length} packages
             </div>
           </div>
           <div>
             <span className="font-medium text-gray-600 dark:text-gray-300">Running:</span>
             <div className="text-gray-900 dark:text-gray-100">
               {(software?.running || []).filter(s => s.running).length} packages
             </div>
           </div>
           <div>
             <span className="font-medium text-gray-600 dark:text-gray-300">Total Cost:</span>
             <div className="text-gray-900 dark:text-gray-100">
               {formatCurrency(calculateSoftwareCost(software, computer) * 1000000)}
             </div>
           </div>
           <div>
             <span className="font-medium text-gray-600 dark:text-gray-300">Status:</span>
             <div className="font-semibold text-green-600 dark:text-green-400">
               Software Ready
             </div>
           </div>
         </div>
       </div>

       {/* Validation Errors */}
       {validation.errors.length > 0 && (
         <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 p-4 rounded-lg">
           <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">Configuration Issues</h4>
           <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
             {validation.errors.map((error, index) => (
               <li key={index}>• {error}</li>
             ))}
           </ul>
         </div>
       )}
     </div>
   );
 };
